// Wires one running match: RemoteGameView fed by `snapshot`, commands sent through the command channel,
// and the shared matchView.ts render loop — including fx.ts itself now (the fx.ts/GameView unification;
// see that module's own header), driven by RemoteGameView exactly like main.ts already drives it from
// LocalGameView. net/remoteFx.ts and net/remoteEntities.ts, the reduced online-only stand-ins this used to
// wire instead, are gone: parity is reached, and two effect systems is worse than either (this task's own
// brief).
import * as THREE from 'three';
import { runMatchView } from '../matchView.js';
import { RemoteGameView } from './remoteGame.js';
import { createCommandChannel } from './commandChannel.js';
import { createFx } from '../fx.js';
import { createClockSync } from './clockSync.js';
import { createNetOverlay } from './netOverlay.js';
import { parseMatchStats } from '../../shared/matchStats.js';
import { isValidReplayId } from '../../shared/replay/format.js';
import { freshReplayLink } from '../replay/replayLink.js';
import type { createEngraver } from '../engrave.js';
import type { buildMap } from '../mapView.js';
import type { Sound } from '../sound.js';
import type { Connection } from './connection.js';
import type { ClientMessage, ServerMessage } from '../../shared/protocol.js';
import { t } from '../i18n/index.js';

const RESEND_INTERVAL = 0.25;  // a periodic re-send of whatever's still unacked, independent of new input
const STATS_WINDOW_MS = 1000;  // the window the `?net` overlay's mean/worst/corrections-per-second cover
// How often (and by how much) the adaptive interp delay must move before telling the server — it changes
// slowly by design (net/entityInterp.ts's hysteresis), so there is nothing to gain from reporting every
// frame, only rate-limit budget (config.ts's per-seat message bucket) to waste.
const INTERP_REPORT_INTERVAL_MS = 1000;
const INTERP_REPORT_THRESHOLD_MS = 5;
// Clock-sync warm-up during the pre-match countdown: once its own warm-up is over, connection.ts pings only
// every few seconds, so the first sample of this match's clockSync could otherwise land after play starts
// and snap the clock mid-walk. These put any snap inside the countdown, while nothing moves.
const COUNTDOWN_PINGS = 4;
const COUNTDOWN_PING_INTERVAL_MS = 250;

export type OnlineMatchDeps = {
  scene: THREE.Scene; camera: THREE.Camera; eng: ReturnType<typeof createEngraver>;
  stageEl: HTMLElement; hudEl: HTMLElement; canvas: HTMLCanvasElement; mapView: ReturnType<typeof buildMap>;
  params: URLSearchParams;
  /** Owned by net/entry.ts, one per page session, not per match: an AudioContext is a scarce, easily
      leaked browser resource, and the online lobby's "play again" loop can start several matches without
      ever reloading the page. */
  sound: Sound;
  connection: Connection;
  matchStart: Extract<ServerMessage, { type: 'matchStart' }>;
  /** yourAckSeq from the roomState this matchStart arrived alongside, if this is a mid-match reconnect —
      the command channel's own seq counter must resume from there, or every command is dropped as stale
      forever (see protocol.ts's RoomState doc comment). Undefined/null on a fresh match start. */
  resumeAckSeq: number | null;
  onEnded(): void;
  /** «Смотреть повтор» on the results screen; default: open the replay page in this tab. */
  watchReplay?(id: string): void;
};

export function startOnlineMatch(deps: OnlineMatchDeps) {
  const { scene, camera, eng, stageEl, hudEl, canvas, mapView, params, sound, connection, matchStart, resumeAckSeq, onEnded } = deps;
  const watchReplay = deps.watchReplay ?? ((id: string) => location.assign(freshReplayLink(id)));
  /** matchEnd's `replayId`, once the server has named one. */
  let replayId: string | null = null;

  let view!: RemoteGameView;  // assigned just below — the callback only ever runs after that (see net/entry.ts's own use of this pattern)
  const commandChannel = createCommandChannel(msg => connection.send(msg as ClientMessage), () => view.inputTick);
  if (resumeAckSeq !== null) commandChannel.resumeFrom(resumeAckSeq);

  view = new RemoteGameView(matchStart.roster, matchStart.yourHero!, matchStart.seed, (_heroId: number, cmd) => commandChannel.send(cmd));
  view.startCountdown(matchStart.countdown ?? 0);
  const fx = createFx(scene, view, sound);
  const clockSync = createClockSync();
  const netOverlay = params.has('net') ? createNetOverlay(stageEl) : null;
  const errWindow: { atMs: number; err: number }[] = [];
  const snapTimestampsMs: number[] = [];
  let lastSnapshotAtMs = performance.now();
  let lastReportedInterpDelayMs = -1;
  let lastInterpReportAtMs = 0;

  let resendAcc = 0;
  let ended = false;
  let countdownPings = 0, lastCountdownPingAtMs = 0;

  const handle = runMatchView({
    scene, camera, eng, stageEl, hudEl, canvas, mapView, sound, view, params,
    stepSim(dt) {
      view.tick(dt);
      if (view.countdown > 0 && countdownPings < COUNTDOWN_PINGS && Date.now() - lastCountdownPingAtMs >= COUNTDOWN_PING_INTERVAL_MS) {
        lastCountdownPingAtMs = Date.now();
        countdownPings++;
        connection.send({ type: 'ping', t: lastCountdownPingAtMs });
      }
      // Steer the client's own tick counter toward "the server's current tick + lead", rather than let it
      // free-run forever: a big jump (a tab stall, or simply before the very first pong ever lands) snaps
      // outright and re-bases the input buffer so tick comparisons stay meaningful (see commandChannel.ts's
      // rebaseTicks); a small drift is slewed away (see remoteGame.ts's steerClock).
      let justRebased = false;
      if (clockSync.sampleCount > 0) {
        const jump = view.steerClock(clockSync.estimatedServerTick(Date.now()) + clockSync.leadTicks, dt);
        if (jump !== 0) { commandChannel.rebaseTicks(jump); justRebased = true; }
      }
      view.reconcile(dt, commandChannel.standingOrder, commandChannel.unacked);
      // The server-tick estimate only measures the delay reported below: it is a bare 0 before the first
      // pong (clockSync.ts's own default), which would read as no delay at all.
      view.advanceOthers(dt, Date.now(), clockSync.sampleCount > 0 ? clockSync.estimatedServerTick(Date.now()) : null);

      const nowMs = Date.now();
      if (nowMs - lastInterpReportAtMs >= INTERP_REPORT_INTERVAL_MS &&
          Math.abs(view.interpDelayMs - lastReportedInterpDelayMs) >= INTERP_REPORT_THRESHOLD_MS) {
        lastInterpReportAtMs = nowMs;
        lastReportedInterpDelayMs = view.interpDelayMs;
        connection.send({ type: 'interpDelay', ms: Math.round(view.interpDelayMs) });
      }

      if (netOverlay) {
        const now = performance.now();
        // `null` (nothing measured yet — see remoteGame.ts's own comment on stats.predictionErrorM) is
        // never pushed: an unmeasured frame diluting the average toward a falsely reassuring low number is
        // the exact failure mode a stage-3 review caught the FIELD's own initial value causing.
        if (view.stats.predictionErrorM !== null) errWindow.push({ atMs: now, err: view.stats.predictionErrorM });
        while (errWindow.length && now - errWindow[0]!.atMs > STATS_WINDOW_MS) errWindow.shift();
        // A snap in the SAME frame as a hard tick rebase just above is a MECHANICAL consequence of that
        // rebase, not a network-driven misprediction: rebasing clientTick forward by many ticks in one step
        // makes the very next predict() replay that many ticks' worth of standing-order movement at once,
        // which is legitimately far from wherever the render was sitting a frame ago — real, but not
        // evidence the PREDICTION disagreed with anything the server reported (predictionErrorM, above,
        // already measures that separately and correctly reads unaffected by it). Counting it here anyway
        // is exactly the "corrections/s implies a real error somewhere, but pred err reads 0" mismatch a
        // live review caught: the overlay's headline correction rate is about network-caused corrections,
        // so a purely local clock catch-up must not inflate it.
        if (view.stats.lastCorrectionWasSnap && !justRebased) snapTimestampsMs.push(now);
        while (snapTimestampsMs.length && now - snapTimestampsMs[0]! > STATS_WINDOW_MS) snapTimestampsMs.shift();
        netOverlay.update({
          rttMs: clockSync.rttMs, jitterMs: clockSync.jitterMs, leadTicks: clockSync.leadTicks,
          unacked: commandChannel.unacked.length,
          predErrMeanM: errWindow.length ? errWindow.reduce((a, b) => a + b.err, 0) / errWindow.length : null,
          predErrMaxM: errWindow.length ? errWindow.reduce((a, b) => Math.max(a, b.err), 0) : null,
          correctionsPerSec: snapTimestampsMs.length * (1000 / STATS_WINDOW_MS),
          snapshotAgeMs: now - lastSnapshotAtMs,
          interpDelayMs: view.interpDelayMs,
          interpStalls: view.interpStalls,
        });
      }

      resendAcc += dt;
      // Only while the socket is actually open: resending into a queued, capped connection (net/
      // connection.ts) during a disconnect just races the real reconnect flush for no gain — the very
      // next `resend()` after reconnecting covers whatever this one would have.
      if (resendAcc >= RESEND_INTERVAL) { resendAcc = 0; if (connection.status === 'open') commandChannel.resend(); }
      return Math.min(dt, 0.25);
    },
    afterStep(rootOf) { fx.update(rootOf); },
    onBack() { finish(); },
    backLabel: t('net.toLobby'),
    resultsSecondary: () => {
      const id = replayId;
      return id ? { label: t('net.watchReplay'), run: () => watchReplay(id) } : null;
    },
  });

  function finish() {
    if (ended) return;
    ended = true;
    netOverlay?.destroy();
    // The rigs/rings/listeners/audio-context teardown handle.stop() already does covers matchView.ts's own
    // state; it never touches fx.ts's own meshes, added to the SAME persistent `scene` this module owns —
    // left alone, a match ending mid-effect (a pool still growing, a status ring still up) orphans that
    // mesh in `scene` forever, visible on top of every match played afterward in this tab (the exact bug
    // this method was first added to fix, before the fx.ts/GameView unification made it fx.ts's own).
    fx.destroy();
    handle.stop();
    onEnded();
  }

  // The server closes the room right behind matchEnd (room.ts's onMatchEnded destroys it immediately), and
  // either can arrive first. A matchEnd with usable stats puts up the results screen (hud.ts), which stays
  // until the player presses «В лобби» / Esc — nothing leaves on a timer then. Without results (a room
  // closed by an aborted match, or stats that didn't parse) the plain banner holds 2.5 s and returns to the
  // lobby by itself, as before; a matchEnd landing inside that window still cancels the auto-return.
  let finishScheduled = false;
  function scheduleFinish() {
    if (finishScheduled) return;
    finishScheduled = true;
    setTimeout(() => { if (!view.matchStats) finish(); }, 2500);
  }
  function showResults(msg: Extract<ServerMessage, { type: 'matchEnd' }>) {
    const stats = parseMatchStats(msg.stats);
    if (!stats) return scheduleFinish();
    // The final snapshot normally set these already; a matchEnd is authoritative if it did not.
    if (!view.winner) { view.winner = msg.winner; view.score = { ...msg.score }; }
    if (isValidReplayId(msg.replayId)) replayId = msg.replayId;
    view.matchStats = stats;
  }

  return {
    handleMessage(msg: ServerMessage) {
      switch (msg.type) {
        case 'snapshot':
          // Entities/pools/etc. need no separate update call any more — RemoteGameView.applySnapshot()
          // stores them, and fx.ts's own update() (afterStep, below) reads view.pools/.totems/etc. fresh
          // every render frame, exactly like it already reads view.heroes.
          view.applySnapshot(msg);
          commandChannel.ack(msg.ackSeq);
          lastSnapshotAtMs = performance.now();
          return;
        case 'pong':
          // connection.ts already sends the ping this replies to (every few seconds) for its own RTT
          // display — net/clockSync.ts is the real consumer, turning the same round trips into a smoothed
          // offset/jitter estimate and the tick lead reconcile() runs on.
          clockSync.onPong(msg.t, Date.now(), msg.tick);
          return;
        case 'matchEnd':
          if (!ended) showResults(msg);
          return;
        case 'roomClosed':
          scheduleFinish();
          return;
        default:
          return;
      }
    },
    stop: finish,
  };
}
